import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { useFleetOptions } from "../hooks/useFleetOptions.js";
import { useVenueOptions } from "../hooks/useVenueOptions.js";
import { formatContentBundleLabel } from "../lib/contentBundleLabel.js";
import {
  type DeviceSortKey,
  filterByStatus,
  nextSortState,
  parseDeviceSortKey,
  parseSortDir,
  parseStatusFilter,
  sortDevices,
  type StatusFilter,
} from "../lib/deviceListSort.js";

interface Device {
  id: string;
  name: string;
  serial_number: string;
  status: string;
  venue_id?: string;
  fleet_id?: string;
  hardware_serial?: string;
  model?: string;
  android_version?: string;
  ip_address?: string;
  dpc_version?: string;
  app_url?: string;
  last_heartbeat_at?: string;
  config?: Record<string, unknown>;
  applied_config?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/** Serials are long and share a prefix; the tail is what distinguishes them. */
function shortSerial(serial: string): string {
  return serial.length > 8 ? serial.slice(-8) : serial;
}

function statusBadge(status: string) {
  const styles: Record<string, string> = {
    pending: "bg-amber-500/20 text-amber-400",
    provisioned: "bg-blue-500/20 text-blue-400",
    online: "bg-green-500/20 text-green-400",
    rejected: "bg-red-500/20 text-red-400",
  };
  const style = styles[status] ?? "bg-gray-500/20 text-gray-400";
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${style}`}>
      {status}
    </span>
  );
}

const SORT_HEADERS: { key: DeviceSortKey; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "serial_number", label: "Serial Number" },
  { key: "status", label: "Status" },
  { key: "fleet", label: "Fleet" },
  { key: "venue", label: "Venue" },
  { key: "content_bundle", label: "Content Bundle" },
  { key: "model", label: "Model" },
  { key: "dpc_version", label: "Kiosk Version" },
  { key: "ip_address", label: "IP" },
  { key: "last_heartbeat_at", label: "Last Heartbeat" },
  { key: "created_at", label: "Created" },
];

export function Devices() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.is_platform_admin ?? false;
  const { options: fleetOptions, loaded: fleetsLoaded } = useFleetOptions();
  const { options: venueOptions, loaded: venuesLoaded } = useVenueOptions();
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const fleetFilter = params.get("fleet_id") ?? "";
  const statusFilter: StatusFilter = parseStatusFilter(params.get("status"));
  // No default column — first click sets asc and sticks in the URL.
  const sortKey = parseDeviceSortKey(params.get("sort"));
  const sortDir = parseSortDir(params.get("dir"));

  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  function patchQuery(patch: Record<string, string | null>) {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    navigate(`?${next.toString()}`, { replace: true });
  }

  // Fleets and venues are fetched alongside the devices, so a row can render
  // before either name is known. "—" is reserved for a device genuinely in no
  // fleet or venue; while the names are in flight the cell says so instead.
  const fleetName = useCallback(
    (fleetId?: string) => {
      if (!fleetId) return "—";
      if (!fleetsLoaded) return "…";
      return fleetOptions.find((f) => f.id === fleetId)?.name ?? "Unknown";
    },
    [fleetOptions, fleetsLoaded],
  );

  const venueName = useCallback(
    (venueId?: string) => {
      if (!venueId) return "—";
      if (!venuesLoaded) return "…";
      return venueOptions.find((v) => v.id === venueId)?.name ?? "Unknown";
    },
    [venueOptions, venuesLoaded],
  );

  const fetchDevices = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/devices"]["get"]({
        parameters: fleetFilter ? { fleet_id: fleetFilter } : {},
        body: null,
      });
      if (res.code === 200) {
        setDevices(res.body as Device[]);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [fleetFilter]);

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(fetchDevices, 10_000);
    return () => clearInterval(interval);
  }, [fetchDevices]);

  const acceptDevice = useCallback(
    async (id: string) => {
      setActionLoading(id);
      try {
        await apiClient["/api/v1/admin/devices/{id}/accept"]["post"]({
          parameters: { id },
          body: null,
        });
        fetchDevices();
      } catch {
        // Ignore
      } finally {
        setActionLoading(null);
      }
    },
    [fetchDevices],
  );

  const rejectDevice = useCallback(
    async (id: string) => {
      setActionLoading(id);
      try {
        await apiClient["/api/v1/admin/devices/{id}/reject"]["post"]({
          parameters: { id },
          body: null,
        });
        fetchDevices();
      } catch {
        // Ignore
      } finally {
        setActionLoading(null);
      }
    },
    [fetchDevices],
  );

  const pendingDevices = devices.filter((d) => d.status === "pending");
  const rejectedDevices = devices.filter((d) => d.status === "rejected");
  const activeDevices = useMemo(() => {
    const active = devices.filter((d) => d.status !== "pending" && d.status !== "rejected");
    const filtered = filterByStatus(active, statusFilter);
    if (!sortKey) return filtered;
    return sortDevices(filtered, sortKey, sortDir, { fleetName, venueName });
  }, [devices, statusFilter, sortKey, sortDir, fleetName, venueName]);

  function onSortClick(key: DeviceSortKey) {
    const next = nextSortState(sortKey, sortDir, key);
    patchQuery({ sort: next.key, dir: next.dir });
  }

  return (
    <main className="p-6">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Devices</h1>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        {fleetOptions.length > 1 && (
          <select
            aria-label="Filter by fleet"
            value={fleetFilter}
            onChange={(e) => patchQuery({ fleet_id: e.target.value || null })}
            className="bg-surface border border-border rounded px-2 py-1 text-sm"
          >
            <option value="">All fleets</option>
            {fleetOptions.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) =>
            patchQuery({ status: e.target.value === "all" ? null : e.target.value })
          }
          className="bg-surface border border-border rounded px-2 py-1 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>
      </div>

      {loading && <p className="text-text-muted text-sm">Loading devices...</p>}

      {!loading && devices.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No devices registered.</p>
          <p className="text-text-muted text-sm mt-1">
            Devices appear here when provisioned via ADB or when they register via the handshake
            flow.
          </p>
        </div>
      )}

      {!loading && pendingDevices.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-amber-400 mb-3">
            Pending Approval ({pendingDevices.length})
          </h2>
          <div className="space-y-3">
            {pendingDevices.map((d) => (
              <div
                key={d.id}
                className="bg-surface-raised border border-amber-500/30 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-text-primary">{d.name}</span>
                      {statusBadge(d.status)}
                    </div>
                    <div className="text-sm text-text-muted space-y-0.5">
                      {d.hardware_serial && <div>Serial: {d.hardware_serial}</div>}
                      {d.model && <div>Model: {d.model}</div>}
                      {d.ip_address && <div>IP: {d.ip_address}</div>}
                      {d.android_version && <div>Android: {d.android_version}</div>}
                      {d.dpc_version && <div>Kiosk: v{d.dpc_version}</div>}
                      <div>Registered: {formatDate(d.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => acceptDevice(d.id)}
                      disabled={actionLoading === d.id}
                      className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-500 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === d.id ? "..." : "Accept"}
                    </button>
                    <button
                      onClick={() => rejectDevice(d.id)}
                      disabled={actionLoading === d.id}
                      className="px-3 py-1.5 text-sm rounded-md bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && (activeDevices.length > 0 || statusFilter !== "all") && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-text-primary mb-3">
            Active ({activeDevices.length}
            {statusFilter !== "all" ? ` · ${statusFilter}` : ""})
          </h2>
          {activeDevices.length === 0 ? (
            <p className="text-text-muted text-sm">No devices match this status filter.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border">
                    {SORT_HEADERS.map(({ key, label }, index) => {
                      const active = sortKey === key;
                      const indicator = active ? (sortDir === "asc" ? " ↑" : " ↓") : "";
                      const isLast = index === SORT_HEADERS.length - 1;
                      return (
                        <th
                          key={key}
                          className={`pb-2 text-sm font-medium text-text-muted ${isLast ? "" : "pr-6"}`}
                          aria-sort={
                            active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
                          }
                        >
                          <button
                            type="button"
                            onClick={() => onSortClick(key)}
                            className="inline-flex items-center gap-0.5 text-left hover:text-text-primary transition-colors"
                            aria-label={`Sort by ${label}`}
                          >
                            <span>{label}</span>
                            <span aria-hidden="true" className="text-accent tabular-nums w-3">
                              {indicator.trim()}
                            </span>
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {activeDevices.map((d) => {
                    const overrideUrl =
                      typeof d.config?.content_bundle_url === "string"
                        ? d.config.content_bundle_url
                        : null;
                    return (
                      <tr
                        key={d.id}
                        className="border-b border-border/50 hover:bg-surface-raised/50"
                      >
                        <td className="py-3 pr-6">
                          <a
                            href={`/devices/${d.id}`}
                            className="text-text-primary hover:text-accent transition-colors no-underline font-medium"
                          >
                            {d.name}
                          </a>
                          {d.hardware_serial && (
                            <div className="text-xs text-text-muted font-mono">
                              {d.hardware_serial}
                            </div>
                          )}
                        </td>
                        <td
                          className="py-3 pr-6 text-sm text-text-secondary font-mono"
                          title={d.serial_number}
                        >
                          {shortSerial(d.serial_number)}
                        </td>
                        <td className="py-3 pr-6">{statusBadge(d.status)}</td>
                        <td className="py-3 pr-6 text-sm text-text-secondary">
                          {fleetName(d.fleet_id)}
                        </td>
                        <td className="py-3 pr-6 text-sm text-text-secondary">
                          {venueName(d.venue_id)}
                        </td>
                        <td className="py-3 pr-6 text-sm whitespace-nowrap">
                          {overrideUrl ? (
                            isPlatformAdmin ? (
                              <a
                                href={`/devices/${d.id}#content-bundle`}
                                title={overrideUrl}
                                className="text-amber-400 hover:text-amber-300 transition-colors no-underline"
                              >
                                <span aria-hidden="true">⚠️ </span>
                                {formatContentBundleLabel(overrideUrl)}
                              </a>
                            ) : (
                              <span className="text-amber-400" title={overrideUrl}>
                                <span aria-hidden="true">⚠️ </span>
                                {formatContentBundleLabel(overrideUrl)}
                              </span>
                            )
                          ) : (
                            <span className="text-text-muted">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-6 text-sm text-text-secondary">{d.model ?? "—"}</td>
                        <td className="py-3 pr-6 text-sm text-text-secondary font-mono whitespace-nowrap">
                          {d.dpc_version || "—"}
                        </td>
                        <td className="py-3 pr-6 text-sm text-text-secondary font-mono">
                          {d.ip_address ?? "—"}
                        </td>
                        <td className="py-3 pr-6 text-sm text-text-muted">
                          {d.last_heartbeat_at ? timeAgo(d.last_heartbeat_at) : "Never"}
                        </td>
                        <td className="py-3 text-sm text-text-muted">{formatDate(d.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!loading && rejectedDevices.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-text-muted mb-3">
            Rejected ({rejectedDevices.length})
          </h2>
          <div className="overflow-x-auto opacity-60">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-2 text-sm font-medium text-text-muted">Name</th>
                  <th className="pb-2 text-sm font-medium text-text-muted">Model</th>
                  <th className="pb-2 text-sm font-medium text-text-muted">Rejected</th>
                </tr>
              </thead>
              <tbody>
                {rejectedDevices.map((d) => (
                  <tr key={d.id} className="border-b border-border/50">
                    <td className="py-3 text-text-secondary">{d.name}</td>
                    <td className="py-3 text-sm text-text-muted">{d.model ?? "—"}</td>
                    <td className="py-3 text-sm text-text-muted">{formatDate(d.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </main>
  );
}
